<template>
  <div
    v-tooltip.right="{
      content: value.tooltip || (value.completed ? $t('completed') : undefined),
      boundariesElement: body,
      hideOnTargetClick: false,
    }"
    class="action-button-wrapper"
  >
    <ButtonComponent
      :promise="promise"
      :disabled="value.disabled"
      @click="onClick"
      class="action-button"
      :class="{ 'action-button--completed': value.completed }"
      ref="button"
    >
      <template #label>
        {{ value.name || value.label }}
        <Modal v-if="dialog" @close="dialog = false" @submit="submit" :value="data" :promise="promise" />
      </template>
      <template v-if="icon" #icon="props">
        <FontAwesomeIcon v-if="props.icon" v-bind="props.icon" />
        <component v-else :is="icon.component" v-bind="icon.props" />
        <FontAwesomeIcon
          v-if="value.completed"
          :icon="['fas', 'check-circle']"
          size="lg"
          class="action-button__completed-icon"
        />
      </template>
    </ButtonComponent>
  </div>
</template>

<script>
import { mergeDeepRight } from 'ramda'
import { ObjectToDotNotation, ExtractString, DownloadUrl, isObject } from '@/utils'

import Card from '@/components/Card'
import Modal from './Modal'
import { ButtonComponent } from 'vue-elder-button'
import tracker from '@/tracking'
import { ParseUrl } from './utils'
import router from '@/router'

export default {
  name: 'ActionItem',
  props: {
    value: Object,
  },
  inject: ['getQuery', 'getPlacement'],
  data() {
    return {
      promise: null,
      dialog: false,
      body: document.body,
      data: null,
    }
  },
  computed: {
    query() {
      return this.getQuery()
    },
    placement() {
      return this.getPlacement()
    },

    qs() {
      let p = new URLSearchParams()

      if (this.placement) p.append('placement', this.placement)

      Object.entries(ObjectToDotNotation(mergeDeepRight(this.$route.query, this.query || {}))).forEach(
        ([key, value]) => {
          if (value === undefined) return
          if (value instanceof Array) return value.forEach(v => p.append(key, v))
          return p.append(key, value)
        },
      )
      return p.toString()
    },
    icon() {
      if (!this.value.icon) return
      if (typeof this.value.icon === 'string') {
        if (this.value.icon.includes('/')) return { component: 'img', props: { src: this.value.icon } }
        return { component: 'FontAwesomeIcon', props: { icon: this.value.icon } }
      }
      if (this.value.icon instanceof Array) return { component: 'FontAwesomeIcon', props: { icon: this.value.icon } }

      return { component: 'FontAwesomeIcon', props: this.value.icon }
    },
  },
  methods: {
    onSuccess() {
      this.dialog = false
      this.$bus.$emit('actions:refetch', this.placement)
    },
    onClick() {
      this.data = structuredClone(this.value)
      if (this.value.disabled || this.value.completed) return
      if (this.value.options && this.value.options.length) return (this.dialog = true)
      if (isObject(this.value.url)) return this.$router.push(this.value.url)
      return this.submit()
    },
    submit(data = {}) {
      tracker.emit(`${this.data.name}:action`, { placement: this.placement })

      let { method, url } = ParseUrl(this.data.url)
      if (!url.includes('?')) url = `${url}?${this.qs}`

      this.promise = fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'kvass-trigger': 'action',
          'kvass-route': this.$route.name,
        },
        credentials: 'include',
        body: JSON.stringify(data),
      }).then(async res => {
        try {
          if (!res.ok && res.status !== 301) throw res

          const Handlers = {
            navigate: value => {
              this.$router.push(value)
            },
            open: async value => {
              window.open(value)
              return this.onSuccess()
            },
            redirect: value => {
              window.location.href = value
            },
            action: value => {
              this.data = {
                name: this.data.name,
                ...value,
              }
            },
            close: () => {
              this.onSuccess()
            },
            emit: value => {
              this.$emit(`action:${(value && value.event) || value}`, value && value.payload)
              this.onSuccess()
            },
            download: value => {
              let url = URL.createObjectURL(value)
              DownloadUrl(url, ExtractString(res.headers.get('Content-Disposition'), /filename=(.*)$/))
              URL.revokeObjectURL(url)

              this.onSuccess()
            },
          }

          if (res.status === 301) return Handlers.open(await res.text())

          if (res.headers.get('Content-Type').includes('application/json')) {
            const { type, value } = await res.json()
            if (!(type in Handlers)) return this.onSuccess()

            return Handlers[type](value)
          }

          if ((res.headers.get('Content-Disposition') || '').startsWith('attachment;'))
            return Handlers.download(await res.blob())

          return this.onSuccess()
        } catch (err) {
          throw err
        }
      })
    },
  },
  mounted() {
    if (this.$route.query.action !== this.value.url) return
    this.onClick()
    this.$router.replace({ query: { ...this.$route.query, action: undefined } })
  },
  components: {
    Card,
    Modal,
    ButtonComponent,
  },
}
</script>

<style lang="scss">
.action-button {
  &--completed,
  &:disabled:not(.elder-button--loading) {
    text-decoration: line-through;
    cursor: not-allowed;

    background-color: transparent;

    .elder-button__label {
      opacity: 0.7;
    }

    &:before {
      content: initial;
    }
  }

  .elder-button__icon {
    display: flex;

    img {
      max-height: 1em;
    }
  }

  &.elder-button--loading {
    img {
      filter: grayscale(100%);
    }
  }

  &__completed-icon {
    color: var(--success);
    position: absolute;
    right: 0;
    top: 0;
    transform: translate(-20%, 20%);
    border-radius: 50%;
    background-color: white;
  }
}
</style>
